import { UiVersion } from "../../uiVersion";

// GUNDI-5639: the URL must always wear the ACTIVE version's vocabulary, so
// switching versions TRANSLATES the path instead of reusing it. Never leave a
// legacy user on a URL that only means something in new vocabulary, or the
// other way round.
//
// Entity-level only: tabs and deep sub-paths are dropped, since the two trees
// do not have the same sub-pages. Lists and the vocabulary-neutral pages
// (`/`, `/sources*`, `/workspaces*`, `/account`) map to themselves.
// Both trees key their entity URLs on the PROVIDER INTEGRATION id, so the id in
// the path carries across unchanged. The only thing still worth asking the API
// is whether a provider has a route at all, which decides between the new UI's
// route page and its connection page.
export type VersionPathResolvers = {
    providerHasRoute: (providerId: string) => Promise<boolean>;
};

export async function mapPathAcrossVersions(
    pathname: string,
    target: UiVersion,
    resolvers: VersionPathResolvers,
): Promise<string> {
    const [head, id] = pathname.split("/").filter(Boolean);

    if (target === "new") {
        // Leaving legacy.
        if (head === "connections") {
            if (!id) return "/";
            if (id === "new") return "/routes/new";
            // No route (or the lookup failed): the connection page still
            // exists in the new UI and links to the route, one click away.
            return (await resolvers.providerHasRoute(id)) ? `/routes/${id}` : `/connections/${id}`;
        }
        if (head === "destinations" || head === "destination") {
            if (!id) return "/connections";
            return id === "new" ? "/connections/new" : `/connections/${id}`;
        }
        return pathname;
    }

    // Leaving the new UI.
    if (head === "routes") {
        if (!id) return "/";
        if (id === "new") return "/connections/new";
        // Straight across: the legacy connection screen was keyed on the same
        // provider integration id the new route URL now carries.
        return `/connections/${id}`;
    }
    if (head === "connections") {
        if (!id) return "/destinations";
        return id === "new" ? "/destinations/new" : `/destinations/${id}`;
    }
    return pathname;
}
